/**
 * LinkedIn serializer — HTML → plain text + URL inlining.
 *
 * LinkedIn /rest/posts `commentary` is plain text only. Rules per
 * docs/Reach/PHASE_1C.md §"LinkedIn":
 *
 *   <strong>, <em>             → strip tags (text only)
 *   <a href="X">label</a>      → `label (X)`; LinkedIn auto-unfurls 1st URL
 *   <h2>, <h3>                 → strip tag, surround with blank lines
 *   <ul>, <ol>                 → •  / 1. Unicode bullets
 *   <blockquote>               → prefix lines with `> `
 *   <code> inline              → wrap in backticks
 *   <pre><code> block          → wrap in triple backticks
 *   <hr>                       → blank line with em-dash separator
 *   <img>                      → strip (image is separate payload)
 *   <br>                       → newline
 *   <p>                        → newline + newline boundary
 *   custom subscribe / paywall → strip
 *
 *   total length: 3000 char hard cap (warning at 90%)
 */

import { parseHtml, tidy, looksLikeHtml } from './common.js';

const MAX_LEN = 3000;
const WARN_AT = Math.floor(MAX_LEN * 0.9);

/**
 * Serialize an HTML fragment to LinkedIn-ready plain text.
 * Returns { text, char_count, warnings, errors }.
 */
export function serializeForLinkedIn(htmlOrText) {
    const input = String(htmlOrText || '');
    if (input.trim() === '') {
        return { text: '', char_count: 0, warnings: [], errors: [] };
    }
    // Plain text passes through (already LinkedIn-ready).
    if (!looksLikeHtml(input)) {
        const text = tidy(input);
        return finalize(text, []);
    }

    const root = parseHtml(input);
    const warnings = [];
    const counts = { stripped_format: 0, stripped_image: 0, stripped_custom: 0, truncated_at_paywall: false };

    // Paywall truncation: LinkedIn has no paywall concept, so everything
    // at or after the paywall divider must be dropped (only the public
    // preview ships). Look for either the custom element or the
    // data-attribute div the TipTap extension emits.
    const paywall = root.querySelector('reach-paywall, [data-reach-paywall]');
    if (paywall) {
        counts.truncated_at_paywall = true;
        let cur = paywall;
        while (cur && cur !== root) {
            let sib = cur.nextSibling;
            while (sib) {
                const next = sib.nextSibling;
                sib.parentNode.removeChild(sib);
                sib = next;
            }
            const parent = cur.parentNode;
            parent.removeChild(cur);
            cur = parent;
        }
    }

    const walk = (node, ctx) => {
        let out = '';
        for (const child of node.childNodes) {
            if (child.nodeType === 3) {
                // Text node — collapse internal whitespace, preserve newlines we added.
                out += child.nodeValue.replace(/[ \t]+/g, ' ');
                continue;
            }
            if (child.nodeType !== 1) continue;

            const tag = child.tagName.toLowerCase();
            switch (tag) {
                case 'p':
                    out += walk(child, ctx).trim() + '\n\n';
                    break;
                case 'br':
                    out += '\n';
                    break;
                case 'h2':
                case 'h3':
                    out += '\n' + walk(child, ctx).trim() + '\n\n';
                    break;
                case 'strong':
                case 'b':
                    if (walk(child, ctx).trim()) counts.stripped_format++;
                    out += walk(child, ctx);
                    break;
                case 'em':
                case 'i':
                    if (walk(child, ctx).trim()) counts.stripped_format++;
                    out += walk(child, ctx);
                    break;
                case 's':
                case 'strike':
                case 'del':
                    if (walk(child, ctx).trim()) counts.stripped_format++;
                    out += walk(child, ctx);
                    break;
                case 'a': {
                    const href = child.getAttribute('href') || '';
                    const label = walk(child, ctx).trim();
                    if (href && label && href !== label) {
                        out += `${label} (${href})`;
                    } else if (href) {
                        out += href;
                    } else {
                        out += label;
                    }
                    break;
                }
                case 'ul':
                case 'ol': {
                    const ordered = tag === 'ol';
                    let i = 1;
                    for (const li of child.children) {
                        if (li.tagName.toLowerCase() !== 'li') continue;
                        const lineText = walk(li, { listOrdered: ordered, listIndex: i }).trim();
                        const prefix = ordered ? `${i}. ` : '• ';
                        out += prefix + lineText + '\n';
                        i++;
                    }
                    out += '\n';
                    break;
                }
                case 'blockquote': {
                    const inner = walk(child, ctx).trim().split('\n').map((l) => `> ${l}`).join('\n');
                    out += inner + '\n\n';
                    break;
                }
                case 'code':
                    if (child.parentElement && child.parentElement.tagName.toLowerCase() === 'pre') {
                        // Handled by 'pre' branch
                        out += child.textContent;
                    } else {
                        out += '`' + child.textContent + '`';
                    }
                    break;
                case 'pre': {
                    const inner = child.textContent.replace(/\s+$/, '');
                    out += '```\n' + inner + '\n```\n\n';
                    break;
                }
                case 'hr':
                    out += '\n— — —\n\n';
                    break;
                case 'img':
                    counts.stripped_image++;
                    // Strip; the image rides as a separate API payload.
                    break;
                case 'reach-subscribe':
                case 'reach-paywall':
                    counts.stripped_custom++;
                    // Strip on LinkedIn; Substack picks these up via its own serializer.
                    break;
                case 'div':
                    // Custom blocks emitted by TipTap render as <div data-reach-*>.
                    // Subscribe block is stripped; paywall was already removed
                    // upstream in the truncation pre-pass.
                    if (child.hasAttribute && (child.hasAttribute('data-reach-subscribe') || child.hasAttribute('data-reach-paywall'))) {
                        counts.stripped_custom++;
                        break;
                    }
                    // Plain unknown div — descend into children.
                    out += walk(child, ctx);
                    break;
                default:
                    out += walk(child, ctx);
            }
        }
        return out;
    };

    let text = walk(root, {});
    text = tidy(text);

    if (counts.stripped_format > 0) {
        warnings.push(`Bold / italic / strikethrough is rendered as plain text on LinkedIn (${counts.stripped_format} instance${counts.stripped_format === 1 ? '' : 's'} stripped).`);
    }
    if (counts.stripped_image > 0) {
        warnings.push(`Inline images aren't supported in LinkedIn commentary; ${counts.stripped_image} image${counts.stripped_image === 1 ? '' : 's'} will publish as separate media attachments instead.`);
    }
    if (counts.stripped_custom > 0) {
        warnings.push('Subscribe / paywall blocks are stripped on LinkedIn — they only render on Substack.');
    }
    if (counts.truncated_at_paywall) {
        warnings.push('LinkedIn has no paywall — only content before the paywall divider was kept.');
    }

    return finalize(text, warnings);
}

function finalize(text, warnings) {
    const chars = text.length;
    const errors = [];
    if (chars > MAX_LEN) {
        errors.push(`Post is ${chars} characters — LinkedIn caps at ${MAX_LEN}. Trim ${chars - MAX_LEN} characters before publishing.`);
    } else if (chars >= WARN_AT) {
        warnings.push(`Close to LinkedIn's ${MAX_LEN}-character cap (${chars} chars).`);
    }
    return {
        text,
        char_count: chars,
        char_cap: MAX_LEN,
        warnings,
        errors,
    };
}
